import { msg } from '@lingui/core/macro';
import { walletSource } from '@/modules/wallet/public';
import { defineEntitySource } from '@/shared/form/entity-source';
import { getCurrentTenant } from '@/shared/http/client';
import {
  listCreditProductOptions,
  listGovernmentDepartments,
  listModalities,
  listProductRules,
  listSubModalities,
} from './credit-product.api';
import type { AvailableWallets, Modality } from './credit-product.wire';
export const creditProductSource = defineEntitySource({
  key: ['credit-product', 'source'],
  scope: getCurrentTenant,
  search: async (query, signal) =>
    (
      await listCreditProductOptions(
        { tenant: getCurrentTenant(), searchString: query || undefined },
        signal,
      )
    ).data,
  toOption: (item) => ({ value: item.id, label: item.name }),
});

/** `(código || 'Não informado') - descrição` (`creditProductContext.tsx:299-303`). */
const modalityOption = (item: Modality, fallback: string) => ({
  value: item.id,
  label: `(${item.code ? String(item.code) : fallback}) - ${item.description ?? ''}`,
});

export const modalitySource = defineEntitySource<Modality>({
  key: ['credit-product', 'modality'],
  search: async (query, signal) =>
    (await listModalities({ page: 0, size: 10, descriptionModality: query || undefined }, signal))
      .data,
  toOption: (item, i18n) => modalityOption(item, i18n._(msg`Não informado`)),
});

/** Sem `codeModalite` o legado não consulta (`creditProductContext.tsx:360`). */
export function subModalitySource(codeModalite: () => number | undefined) {
  return defineEntitySource<Modality>({
    key: ['credit-product', 'sub-modality'],
    scope: () => codeModalite() ?? null,
    search: async (query, signal) => {
      const code = codeModalite();
      if (code === undefined) return [];
      return (
        await listSubModalities(
          { page: 0, size: 10, codeModalite: code, descriptionSubModality: query || undefined },
          signal,
        )
      ).data;
    },
    toOption: (item, i18n) => modalityOption(item, i18n._(msg`Não informado`)),
  });
}

/** Regras da Central (`producRulesContext.tsx:73-230`): 2 crédito, 1 compliance, 3 dinâmica. */
export function productRulesSource(rulesType: 1 | 2 | 3) {
  return defineEntitySource({
    key: ['credit-product', 'product-rules', rulesType],
    search: async (query, signal) =>
      (await listProductRules({ name: query || undefined, rulesType }, signal)).data,
    toOption: (item) => ({ value: item.id, label: item.name }),
  });
}

/** `governmentDepartmentContext.tsx:53-56`. */
export const governmentDepartmentSource = defineEntitySource({
  key: ['credit-product', 'government-department'],
  scope: getCurrentTenant,
  search: async (query, signal) =>
    (
      await listGovernmentDepartments(
        { searchString: query || undefined, tenant: getCurrentTenant() },
        signal,
      )
    ).data,
  toOption: (item, i18n) => ({ value: item.id, label: item.name ?? i18n._(msg`Não informado`) }),
});

export interface WalletChoice {
  readonly code: string;
  readonly name: string;
}

/**
 * Carteira do produto (`InformationTabCreditProductContainer.tsx:89-143`): com a política restrita,
 * só as carteiras de `AvailableWallets`; sem política resolvida, nada; senão, a busca do Billing.
 */
export function productWalletSource(available: () => AvailableWallets | null | undefined) {
  const billing = walletSource();
  return defineEntitySource<WalletChoice>({
    key: ['credit-product', 'wallet'],
    scope: () => {
      const policy = available();
      return policy === undefined ? 'pending' : policy?.restricted ? policy.wallets.map((w) => w.code) : 'open';
    },
    search: async (query, signal) => {
      const policy = available();
      if (policy?.restricted) {
        const term = query.trim().toLowerCase();
        return policy.wallets
          .map((wallet) => ({ code: String(wallet.code), name: `(${String(wallet.code)}) - ${wallet.name ?? ''}` }))
          .filter((wallet) => term === '' || wallet.name.toLowerCase().includes(term));
      }
      if (policy === undefined) return [];
      return (await billing.search(query, signal)).map((wallet) => ({
        code: String(wallet.walletCode),
        name: `(${String(wallet.walletCode)}) - ${wallet.beneficiaryName ?? ''}`,
      }));
    },
    toOption: (item) => ({ value: item.code, label: item.name }),
  });
}
